<template>
  <div>
    <Datepicker
      :key="reloaded"
      v-model="date"
      :locale="datePickerLocale"
      :now-button-label="$t('components.datepicker.nowText')"
      :partial-range="false"
      :placeholder="$t('components.datepicker.placeholder')"
      :style="cssProps"
      auto-apply
      format="dd/MM/yyyy | HH:mm"
      preview-format="dd/MM/yyyy | HH:mm"
      :range="range"
      show-now-button
      :time-picker-inline="timePickerInline"
      @update:modelValue="updateDate"
      v-bind="startTime ? { 'start-time': startTime } : {}"
      :required="required"
      :is-24="true"
    >
      <template
        #right-sidebar
        v-if="showCustomTimeButtons"
      >
        <div class="custom-buttons">
          <button
            type="button"
            @click.stop="setTime(7, 0)"
          >
            07:00
          </button><br>
          <button
            type="button"
            @click.stop="setTime(19, 0)"
          >
            19:00
          </button>
        </div>
      </template>
    </Datepicker>
  </div>
</template>

<script lang="ts">
import {computed, defineComponent, PropType, watch} from 'vue';
import { VueDatePicker as Datepicker } from '@vuepic/vue-datepicker';
import {useI18n} from 'vue-i18n';
import dayjs from 'dayjs';
import { getDatePickerLocale } from '@/services/date-picker-locale';

export default defineComponent({
  name: 'DatePicker',
  components: { Datepicker },
  emits: ['emitDate', 'update:modelValue'],
  data(): { date: any } {
    return {
      date: this.normalizeInput(this.modelValue ?? this.value ?? this.defaultDate)
    }
  },
  props: {
    width: { type: String as PropType<string>, default: '100%' },
    defaultDate: {type: Array as PropType<any[]>, default: null},
    range: {type: Boolean, default: false},
    startTime: {
      type: Object as PropType<{ hours: number; minutes: number }>,
      default: null,
    },
    modelValue: { type: [String, Number, Date, Array] as PropType<any>, default: null },
    value: { type: [String, Number, Date, Array] as PropType<any>, default: null },
    required: {type: Boolean, default: false},
    showCustomTimeButtons: { type: Boolean, default: false },
    timePickerInline: {type: Boolean, default: false}
  },
  computed: {
    cssProps() {
      return {
        'width': this.width
      }
    }
  },
  setup() {
    const i18n = useI18n()
    const datePickerLocale = computed(() => getDatePickerLocale(i18n.locale.value))
    let reloaded = 0

    watch(() => i18n.locale.value, () => {
      reloaded++
    })
    return { datePickerLocale, reloaded }
  },
  watch: {
    modelValue: {
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.date = this.normalizeInput(newValue)
        }
      },
      deep: true,
      immediate: true
    },
    value: {
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          this.date = this.normalizeInput(newValue)
        }
      },
      deep: true,
      immediate: true
    }
  },
  methods: {
    updateDate() {
      this.$emit('update:modelValue', this.date)
      this.$emit('emitDate', this.date)
    },
    normalizeInput(value: any) {
      if (!value) {
        return null
      }

      if (Array.isArray(value)) {
        return value.map((item) => this.normalizeInput(item))
      }

      if (value instanceof Date) {
        return value
      }

      if (typeof value === 'string') {
        const isoCandidate = value.includes('T') ? value : value.replace(' ', 'T')
        const parsedDate = new Date(isoCandidate)
        if (!Number.isNaN(parsedDate.getTime())) {
          return parsedDate
        }
      }

      const parsed = dayjs(value)
      if (parsed.isValid()) {
        return parsed.toDate()
      }

      return value
    },
    setTime(hours: number, minutes: number) {
      const baseDate = this.date instanceof Date
        ? new Date(this.date)
        : new Date()

      const newDate = new Date(baseDate)
      newDate.setHours(hours)
      newDate.setMinutes(minutes)
      newDate.setSeconds(0)
      newDate.setMilliseconds(0)
      this.date = newDate
      this.updateDate()
    }
  },
  mounted() {
    this.updateDate()
  }
})
</script>

<style lang="scss">
.dp__now_button {
  width: 100% !important;
  color: white !important;
  background: var(--dp-primary-color) !important;
  padding: 0 !important;
}

.dp__now_wrap {
  padding: 0 !important;
}

.custom-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 8px 0;
}

.custom-buttons button {
  flex: 1;
  padding: 6px 12px;
  background-color: #f0f0f0;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  transition: background 0.2s;
}

.custom-buttons button:hover {
  background-color: #e0e0e0;
}

</style>
